import { axiosInstance } from "../axios.config";
import type { CancellationReason, RatedRole } from "./ratings.service";

/** PRD Epic 9: escrow and closing (Marketplace side) */
export type EscrowChoice = "partner" | "own" | "terms";
export type EscrowStatus = "awaiting_choice" | "terms_sent" | "opened" | "cancelled";

export interface EscrowWording {
  title: string;
  body: string;
  partnerLabel: string;
  ownLabel: string;
  termsLabel: string;
}

export interface EscrowBackupOption {
  bidId: string;
  amount: number;
  expired: boolean;
  createdAt: string;
}

export interface EscrowView {
  bidId: string;
  listingId: string;
  listingAddress: string;
  myRole: "seller" | "buyer" | "agent" | "admin";
  status: EscrowStatus;
  choice: EscrowChoice | null;
  contact: { company: string | null; name: string | null; email: string | null; phone: string | null } | null;
  termsSentAt: string | null;
  openedAt: string | null;
  cancelledAt: string | null;
  nextStep: "backup" | "relist" | null;
  promotedBidId: string | null;
  /** 9.1 wording: admin config, pending RESPA counsel review */
  wording: EscrowWording;
  partner: { name: string } | null;
  canChoose: boolean;
  canCancel: boolean;
  canChooseNextStep: boolean;
  backups: EscrowBackupOption[];
}

export interface EscrowChoiceInput {
  choice: EscrowChoice;
  company?: string;
  name?: string;
  email?: string;
  phone?: string;
}

export interface EscrowCancellationInput {
  reasonCode: CancellationReason;
  cancelledBy: RatedRole;
  afterDueDiligence: boolean;
  note?: string;
}

export const escrowService = {
  async get(bidId: string): Promise<EscrowView> {
    return (await axiosInstance.get<EscrowView>(`/escrow/deals/${bidId}`)).data;
  },

  async choose(bidId: string, input: EscrowChoiceInput): Promise<EscrowView> {
    return (await axiosInstance.post<EscrowView>(`/escrow/deals/${bidId}/choice`, input)).data;
  },

  async cancel(bidId: string, input: EscrowCancellationInput): Promise<EscrowView> {
    return (await axiosInstance.post<EscrowView>(`/escrow/deals/${bidId}/cancel`, input)).data;
  },

  async promoteBackup(bidId: string, backupBidId: string): Promise<{ promotedBidId: string }> {
    return (await axiosInstance.post<{ promotedBidId: string }>(`/escrow/deals/${bidId}/promote-backup`, { backupBidId }))
      .data;
  },
};
